import { fetchSections, swap, cartRequest, emit } from '@cc/engine';

class CCFacets extends HTMLElement {
  connectedCallback() {
    this.section = this.dataset.section;
    this.form = this.querySelector('[data-facets-form]');
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.form.addEventListener('change', (event) => {
      if (event.target.name !== 'sort_by') this.preview();
    }, { signal });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.apply(this.formUrl());
      this.querySelector('cc-drawer')?.close();
    }, { signal });
    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-facet-sort]')) return;
      this.form.querySelector('input[name="sort_by"]').value = event.target.value;
      this.apply(this.formUrl());
    }, { signal });
    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-facet-link]');
      if (link) {
        event.preventDefault();
        this.querySelector('cc-drawer')?.close();
        this.apply(link.href);
        return;
      }
      const more = event.target.closest('[data-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more.href);
      }
    }, { signal });
    window.addEventListener('popstate', () => this.apply(location.href, false), { signal });
  }

  disconnectedCallback() {
    this.controller.abort();
    this.request?.abort();
  }

  formUrl() {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(this.form)) if (value !== '') params.append(key, value);
    return `${this.form.action}?${params}`;
  }

  fetchRegions(url) {
    this.request?.abort();
    this.request = new AbortController();
    return fetchSections(url, [this.section], this.request.signal);
  }

  async preview() {
    try {
      const docs = await this.fetchRegions(this.formUrl());
      swap(docs[this.section], this, ['apply']);
    } catch {}
  }

  async apply(url, push = true) {
    this.setAttribute('aria-busy', 'true');
    try {
      const docs = await this.fetchRegions(url);
      swap(docs[this.section], this, ['filter-button', 'tags', 'count', 'count-sm', 'grid', 'filters', 'apply']);
      if (push) history.pushState({}, '', url);
    } catch (error) {
      if (error.name !== 'AbortError') location.href = url;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  async loadMore(url) {
    if (this.loading) return;
    this.loading = true;
    try {
      const docs = await fetchSections(url, [this.section]);
      const doc = docs[this.section];
      const incoming = doc.querySelector('[data-region="items"]');
      const list = this.querySelector('[data-region="items"]');
      if (incoming && list) list.append(...[...incoming.children].map((node) => document.importNode(node, true)));
      swap(doc, this, ['more']);
    } catch {
      location.href = url;
    } finally {
      this.loading = false;
    }
  }
}

function cartSections() {
  return [...new Set([...document.querySelectorAll('[data-cart-section]')].map((node) => node.dataset.cartSection))];
}

function renderCart(sections = {}) {
  const active = document.activeElement;
  const key = active?.dataset?.focusKey;
  const inCart = active?.closest('[data-cart-section]');
  const parser = new DOMParser();
  for (const [id, html] of Object.entries(sections)) {
    if (!html) continue;
    const doc = parser.parseFromString(html, 'text/html');
    for (const node of document.querySelectorAll(`[data-cart-section="${id}"]`)) {
      const next = doc.querySelector(`[data-region="${node.dataset.region}"]`);
      if (next) node.replaceWith(document.importNode(next, true));
    }
  }
  if (!inCart) return;
  const scope = document.getElementById('CartDrawer')?.querySelector('dialog[open]') || document;
  (key && scope.querySelector(`[data-focus-key="${key}"]`) || scope.querySelector('[data-cart-focus]'))?.focus();
}

// Cart queue
let cartQueue = Promise.resolve();

function cartUpdate(path, body) {
  const run = cartQueue.then(async () => {
    const data = await cartRequest(path, body, cartSections());
    renderCart(data.sections);
    emit('cart-updated', data);
    return data;
  });
  cartQueue = run.catch(() => {});
  return run;
}

async function refreshCart() {
  const ids = cartSections();
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=${ids.join(',')}`);
  renderCart(await response.json());
}

// Quantity
const timers = new WeakMap();

function queueQuantity(input) {
  const line = input.closest('[data-key]');
  line.setAttribute('aria-busy', 'true');
  clearTimeout(timers.get(input));
  timers.set(input, setTimeout(() => {
    cartUpdate('change.js', { id: line.dataset.key, quantity: Math.max(0, Number(input.value) || 0) })
      .catch(() => { cartError(input); refreshCart(); });
  }, 300));
}

async function addToCart(id, source) {
  const data = await cartUpdate('add.js', { items: [{ id, quantity: 1 }] });
  if (data && document.documentElement.dataset.cart === 'page') {
    location.href = `${window.Shopify?.routes?.root || '/'}cart`;
    return data;
  }
  const drawer = document.getElementById('CartDrawer');
  if (data && drawer && !document.querySelector('[data-region="cart-page"]')) {
    drawer.trigger = source;
    if (!drawer.dialog.open) drawer.open();
    drawer.querySelector('[data-cart-focus]')?.focus();
  }
  return data;
}

function cartError(source) {
  const scope = source.closest('dialog, [data-region="cart-page"]') || document;
  scope.querySelector('[data-cart-error]')?.removeAttribute('hidden');
}

document.addEventListener('click', async (event) => {
  const step = event.target.closest('[data-step]');
  const remove = event.target.closest('[data-remove]');
  const add = event.target.closest('[data-add]');
  if (step) {
    const input = step.parentElement.querySelector('[data-qty]');
    input.value = Math.max(0, Number(input.value) + Number(step.dataset.step));
    queueQuantity(input);
  } else if (remove) {
    event.preventDefault();
    const line = remove.closest('[data-key]');
    line.classList.add('is-removing');
    await cartUpdate('change.js', { id: line.dataset.key, quantity: 0 }).catch(() => { cartError(remove); refreshCart(); });
  } else if (add) {
    await addToCart(Number(add.dataset.add), add).catch(() => cartError(add));
  }
});

document.addEventListener('change', (event) => {
  if (event.target.matches('[data-qty]')) queueQuantity(event.target);
});

// Card swatches
document.addEventListener('click', (event) => {
  const dot = event.target.closest('.card__dot');
  if (!dot) return;
  const card = dot.closest('[data-card]');
  const dots = [...card.querySelectorAll('.card__dot')];
  dots.forEach((item) => item.setAttribute('aria-pressed', item === dot));
  card.querySelector('.card__link').href = dot.dataset.url;
  const image = card.querySelector('.card__media img:not(.frame__alt)');
  if (!image) return;
  image.dataset.srcset ??= image.srcset;
  image.srcset = dot.dataset.srcset || image.dataset.srcset;
  card.toggleAttribute('data-variant-image', Boolean(dot.dataset.srcset));
});

class CCProduct extends HTMLElement {
  connectedCallback() {
    this.section = this.dataset.section;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-option]')) this.select();
    }, { signal });
    this.addEventListener('submit', (event) => {
      event.preventDefault();
      this.add(event.target);
    }, { signal });
    this.addEventListener('click', (event) => {
      if (!event.target.closest('[data-open-reviews]')) return;
      const reviews = this.querySelector('#Reviews');
      if (reviews) reviews.open = true;
    }, { signal });
    // Accordion anchor
    this.addEventListener('click', (event) => {
      const summary = event.target.closest('.pdp__buy summary');
      if (!summary) return;
      const top = summary.getBoundingClientRect().top;
      summary.parentElement.addEventListener('toggle', () => {
        const shift = summary.getBoundingClientRect().top - top;
        if (Math.abs(shift) > 1) window.scrollBy({ top: shift, behavior: 'instant' });
      }, { once: true });
    }, { signal });
  }

  disconnectedCallback() {
    this.controller.abort();
    this.request?.abort();
  }

  async select() {
    const values = [...this.querySelectorAll('[data-option]:checked')].map((input) => input.value);
    const url = `${this.dataset.url}?option_values=${values.join(',')}`;
    this.request?.abort();
    this.request = new AbortController();
    let docs;
    try {
      docs = await fetchSections(url, [this.section], this.request.signal);
    } catch (error) {
      if (error.name !== 'AbortError') location.href = url;
      return;
    }
    swap(docs[this.section], this, ['head', 'options', 'buy']);
    const buy = this.querySelector('[data-region="buy"]');
    const id = buy?.querySelector('[name="id"]')?.value;
    if (id) history.replaceState(history.state, '', `${this.dataset.url}?variant=${id}`);
    if (buy?.dataset.media) this.querySelector('cc-slider')?.go(Number(buy.dataset.media) - 1);
  }

  async add(form) {
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-form-error]');
    if (button.getAttribute('aria-disabled') === 'true' || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    error.hidden = true;
    try {
      const data = await addToCart(Number(new FormData(form).get('id')), button);
      if (data) button.textContent = button.dataset.added;
    } catch {
      error.hidden = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

class CCSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;
    this.prevs = [...this.querySelectorAll('[data-slider-prev]')];
    this.nexts = [...this.querySelectorAll('[data-slider-next]')];
    this.indexes = [...this.querySelectorAll('[data-slider-index]')];
    this.dots = [...this.querySelectorAll('[data-slider-dot]')];
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.prevs.forEach((button) => button.addEventListener('click', () => this.step(-1), { signal }));
    this.nexts.forEach((button) => button.addEventListener('click', () => this.step(1), { signal }));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i), { signal }));
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    }, { signal, passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.step(event.key === 'ArrowRight' ? 1 : -1);
    }, { signal });
    this.observer = new ResizeObserver(() => { this.fit(); this.update(); });
    this.observer.observe(this.track);
    this.autoplay(signal);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    clearInterval(this.timer);
    cancelAnimationFrame(this.frame);
  }

  get slides() {
    return [...this.track.children];
  }

  offset(slide) {
    return slide.offsetLeft - parseFloat(getComputedStyle(this.track).paddingLeft || 0);
  }

  current() {
    const left = this.track.scrollLeft;
    let best = 0;
    this.slides.forEach((slide, i) => {
      if (Math.abs(this.offset(slide) - left) < Math.abs(this.offset(this.slides[best]) - left)) best = i;
    });
    return best;
  }

  perView() {
    const first = this.slides[0];
    return first ? Math.max(1, Math.floor((this.track.clientWidth + 1) / first.getBoundingClientRect().width)) : 1;
  }

  go(index) {
    const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
    if (slide) this.track.scrollTo({ left: this.offset(slide), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  }

  step(direction) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (direction > 0 && this.track.scrollLeft >= max - 1 && this.hasAttribute('data-loop')) return this.go(0);
    this.go(this.current() + direction * this.perView());
  }

  update() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    this.toggleAttribute('data-static', max <= 1);
    const index = this.current();
    this.indexes.forEach((node) => { node.textContent = index + 1; });
    this.dots.forEach((dot, i) => (i === index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current')));
    this.prevs.forEach((button) => { button.disabled = this.track.scrollLeft <= 1; });
    this.nexts.forEach((button) => { button.disabled = !this.hasAttribute('data-loop') && this.track.scrollLeft >= max - 1; });
  }

  // Banner fit
  fit() {
    if (!this.hasAttribute('data-banner-fit')) return;
    this.removeAttribute('data-stacked');
    const tooTall = this.slides.some((slide) => {
      const ratio = parseFloat(getComputedStyle(slide).getPropertyValue('--ratio')) || 1;
      return slide.querySelector('[data-banner-copy]').offsetHeight > slide.clientWidth / ratio + 1;
    });
    this.toggleAttribute('data-stacked', tooTall);
  }

  autoplay(signal) {
    const seconds = Number(this.dataset.autoplay);
    if (!seconds || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let paused = false;
    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    this.addEventListener('pointerenter', pause, { signal });
    this.addEventListener('pointerleave', resume, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', resume, { signal });
    let stopped = false;
    this.querySelector('[data-slider-pause]')?.addEventListener('click', (event) => {
      stopped = !stopped;
      event.currentTarget.setAttribute('aria-pressed', stopped);
    }, { signal });
    this.timer = setInterval(() => {
      if (!paused && !stopped && !document.hidden) this.step(1);
    }, seconds * 1000);
  }
}

class CCRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.children.length) return;
    this.observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      this.observer.disconnect();
      this.load();
    }, { rootMargin: '0px 0px 400px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = doc.querySelector('cc-recommendations');
      if (next) this.replaceChildren(...[...next.children].map((node) => document.importNode(node, true)));
    } catch {
      this.hidden = true;
    }
  }
}

class CCSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.idle = this.querySelector('[data-search-default]');
    this.status = this.querySelector('[data-search-status]');
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(), 250);
    }, { signal });
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-drawer-open="SearchOverlay"]')) return;
      const bottom = document.querySelector('[data-header]')?.getBoundingClientRect().bottom || 0;
      this.closest('cc-drawer').style.setProperty('--search-top', `${Math.max(0, bottom)}px`);
    }, { signal });
  }

  disconnectedCallback() {
    this.controller.abort();
    this.request?.abort();
  }

  async search() {
    const terms = this.input.value.trim();
    this.request?.abort();
    if (!terms) {
      this.results.hidden = true;
      this.idle.hidden = false;
      return;
    }
    this.request = new AbortController();
    const params = new URLSearchParams({ q: terms, 'resources[type]': 'product,article', 'resources[limit]': '4', 'resources[options][prefix]': 'last', section_id: 'predictive-search' });
    try {
      const response = await fetch(`${this.dataset.url}?${params}`, { signal: this.request.signal });
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const found = doc.querySelector('[data-region="predictive"]');
      this.results.replaceChildren(...(found ? [document.importNode(found, true)] : []));
      this.status.textContent = found?.querySelector('[data-search-count]')?.textContent || '';
      this.results.hidden = false;
      this.idle.hidden = true;
    } catch (error) {
      if (error.name !== 'AbortError') this.results.hidden = true;
    }
  }
}

class CCTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab) this.select(tab);
    }, { signal });
    this.addEventListener('keydown', (event) => {
      const index = this.tabs.indexOf(document.activeElement);
      if (index < 0 || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      const next = this.tabs[(index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length];
      this.select(next);
      next.focus();
    }, { signal });
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  select(tab) {
    for (const item of this.tabs) {
      const selected = item === tab;
      item.setAttribute('aria-selected', selected);
      item.tabIndex = selected ? 0 : -1;
      document.getElementById(item.getAttribute('aria-controls')).hidden = !selected;
    }
  }
}

document.addEventListener('change', (event) => {
  const shown = event.target.closest('.tool')?.querySelector('.tool__value');
  if (shown) shown.textContent = event.target.selectedOptions[0].text;
  if (event.target.matches('[data-autosubmit]')) event.target.form.requestSubmit();
});

if (!customElements.get('cc-facets')) customElements.define('cc-facets', CCFacets);
if (!customElements.get('cc-product')) customElements.define('cc-product', CCProduct);
if (!customElements.get('cc-slider')) customElements.define('cc-slider', CCSlider);
if (!customElements.get('cc-recommendations')) customElements.define('cc-recommendations', CCRecommendations);
if (!customElements.get('cc-search')) customElements.define('cc-search', CCSearch);
if (!customElements.get('cc-tabs')) customElements.define('cc-tabs', CCTabs);

